import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
} from 'react';

import type {
  AutocompleteType,
  Suggestion,
} from '#/components/Autocomplete/AutocompleteTextInput';
import { AutocompleteTextInput } from '#/components/Autocomplete/AutocompleteTextInput';

import { Field } from './Field/Field';
import type { FieldCustomErrorMatcher } from './Field/FieldCustomErrorMatcher';

type Props = {
  className?: string;
  filterOptions?: string;
  name: string;
  label?: ReactNode;
  description?: ReactNode;
  mapError?: (arg0: string) => ReactNode;
  // oxlint-disable-next-line typescript/no-explicit-any
  onSuggestionSelected?: (...args: any[]) => any;
  autoComplete?: string;
  autocompleteType: AutocompleteType;
  height?: string;
  large?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (
    event: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => void;
  onFocus?: (
    event: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => void;
  customErrorMatch?: FieldCustomErrorMatcher;
  customError?: string | null;
  suggestionComponentFn?: ({ name, value, hit }: Suggestion) => ReactNode;
  shouldRenderSuggestions?: (arg0: string) => boolean;
  onSuggestionsUpdated?: ({
    suggestions,
    value,
  }: {
    suggestions?: Suggestion[];
    value: string;
  }) => void;
  noMargin?: boolean;
  hasError?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  value?: string;
  updateApiKey?: () => Promise<string | void>;
  searchConfig?: {
    apiKey: string;
    appId: string;
  };
  qaId?: string;
} & Pick<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export function AutocompleteTextInputField({
  className,
  name,
  label,
  description,
  type = 'text',
  mapError,
  autoComplete,
  height,
  large,
  customErrorMatch,
  customError,
  noMargin,
  hasError,
  searchConfig,
  qaId,
  ...props
}: Props) {
  const inputProps = autoComplete ? { ...props, autoComplete: 'off' } : props;
  return (
    <Field
      className={className}
      customErrorMatch={customErrorMatch}
      customError={customError}
      inputProps={inputProps}
      noMargin={noMargin}
      name={name}
      label={label}
      description={description}
      mapError={mapError}
      renderInput={({
        value,
        id,
        qaId: fieldQaId,
        hasError: formHasError,
        updateValue,
      }) => (
        <AutocompleteTextInput
          id={id}
          name={name}
          type={type}
          // TODO: Fix this the next time the file is edited.
          // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
          value={value ?? ''}
          hasError={formHasError || Boolean(hasError)}
          height={height}
          qaId={qaId ?? fieldQaId}
          onChange={(e) => {
            updateValue(e.target.value);
          }}
          onKeyPress={(e) => {
            // Prevents form submission for CMS create/edit page.
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          searchConfig={searchConfig}
          large={large}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...inputProps}
        />
      )}
    />
  );
}
